import { useState, useEffect, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { ArrowLeft, Baby, PersonStanding, HandHelping, MapPin, CalendarDays, Mail, Clock, Trophy, Instagram, MessageCircle, X, ChevronLeft, ChevronRight, Camera } from "lucide-react";
import { Link, useLocation } from "react-router-dom";

function LazyGalleryImage({ src, alt, onClick, index }) {
  const imgRef = useRef(null);
  const [loaded, setLoaded] = useState(false);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = imgRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); obs.disconnect(); } },
      { rootMargin: "200px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div
      ref={imgRef}
      data-testid={`gallery-image-${index}`}
      className="relative group cursor-pointer overflow-hidden rounded-xl aspect-square bg-gray-100"
      onClick={onClick}
    >
      {inView && (
        <img
          src={src}
          alt={alt}
          className={`w-full h-full object-cover transition-all duration-700 group-hover:scale-110 ${loaded ? "opacity-100 scale-100" : "opacity-0 scale-105"}`}
          loading="lazy"
          decoding="async"
          onLoad={() => setLoaded(true)}
        />
      )}
      {!loaded && (
        <div className="absolute inset-0 bg-gray-200 animate-pulse rounded-xl" />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
    </div>
  );
}

const mainSponsors = [
  { name: "Kinskofer Holz Lehm Haus", logo: "https://customer-assets.emergentagent.com/job_run-for-ella/artifacts/ioi6nfmn_logo_kinskofer_quer_cmyk_300dpi.avif", url: "https://kinskofer-holzhaus.de/" },
  { name: "Raiffeisenbank Oberpfälzer Jura", logo: "https://customer-assets.emergentagent.com/job_run-for-ella/artifacts/1gy37ljz_Logo_RGB_zweizeilig_links_pos_AKTUELL.avif", url: "https://www.rb-opf-jura.de/startseite.html" },
  { name: "Ernsberger Wohnbau", logo: "https://customer-assets.emergentagent.com/job_run-for-ella/artifacts/1wsatkfn_Ernsberger%20Wohnbau.avif", url: "https://www.ernsberger-wohnbau.de/" },
];

const partnerSponsors = [
  { name: "Betreuungsbüro Ziegaus", logo: "https://customer-assets.emergentagent.com/job_run-for-ella/artifacts/j42eaorf_0db440_1e13ad9a3d7d4d8387ed789e14457764~mv2.avif", url: null },
  { name: "Gasthaus Neugebauer", logo: "https://customer-assets.emergentagent.com/job_run-for-ella/artifacts/ilje43d3_0db440_61fae593f90a47acb4d35719a492c37e~mv2.avif", url: "https://gasthaus-neugebauer.de/" },
  { name: "Fritz Ehrensberger", logo: "https://customer-assets.emergentagent.com/job_run-for-ella/artifacts/hkh5sffd_0db440_aa919eda0b0348beac07ddbb52fcc967~mv2.avif", url: "https://www.ehrensberger-gmbh.de/" },
  { name: "Weidinger Versichert", logo: "https://customer-assets.emergentagent.com/job_run-for-ella/artifacts/j1cexrll_0db440_d125192ca64e4746a349eca7eb2455aa~mv2.avif", url: "https://www.weidinger-versichert.de/" },
  { name: "Autohaus Vasold", logo: "https://customer-assets.emergentagent.com/job_65cff9e4-1fb7-4087-8c22-d5f62489d89d/artifacts/06mgn326_autohausvasold_ohne_Schatten%20%283%29.svg", url: "https://www.autohaus-vasold.de/" },
  { name: "Orange Immobilien", logo: "https://customer-assets.emergentagent.com/job_run-for-ella/artifacts/6vxetdmw_Orange_Immobilien_Logo.avif", url: "https://orange-immobilien.de/immobilienmakler-regensburg/" },
  { name: "Fahrschulen Schuster", logo: "https://customer-assets.emergentagent.com/job_run-for-ella/artifacts/7ol87ty0_FahrschulenSchusterLogo.avif", url: "https://www.fahrschulen-schuster.de/parsberg/" },
  { name: "Formaxx AG", logo: "https://customer-assets.emergentagent.com/job_run-for-ella/artifacts/4ss4m8ju_Formaxx_ag_logo.avif", url: "https://formaxx.de/standort/parsberg/" },
  { name: "Naturalis", logo: "https://customer-assets.emergentagent.com/job_run-for-ella/artifacts/ihvann5b_491951391_987472600252870_7070819750982631720_n%20%281%29.avif", url: "https://www.naturalis-gmbh.de/" },
  { name: "Bit HD", logo: "https://customer-assets.emergentagent.com/job_run-for-ella/artifacts/3fsu2ksv_Bit_HD_Logo.avif", url: "https://www.bit-hd.de/index.php/unternehmen" },
  { name: "Freitag", logo: "/sponsors/freitag.png", url: "https://elektro-freitag.de/" },
  { name: "Neumarkter Lammsbräu", logo: "/sponsors/lammsbraeu.png", url: "https://www.lammsbraeu.de/" },
  { name: "ESS Erlanger Sicherheitsservice", logo: "/sponsors/ess.png", url: "https://www.nwsgmbh.de/organisation/ess-erlanger-sicherheits-service/" },
];

const gallery2025 = [
  { 
    src: "https://customer-assets.emergentagent.com/job_65cff9e4-1fb7-4087-8c22-d5f62489d89d/artifacts/sbxk3wmc_3db4034f-c98f-496f-9cc7-389fd14f5448.JPG",
    alt: "Run for Rett 2025 - Zieleinlauf"
  },
  { 
    src: "https://customer-assets.emergentagent.com/job_65cff9e4-1fb7-4087-8c22-d5f62489d89d/artifacts/ox2v9tmy_f3d72cb2-221f-4ff3-932c-15ca13f80ea8.JPG",
    alt: "Run for Rett 2025 - Läufer beim Start"
  },
  { 
    src: "https://customer-assets.emergentagent.com/job_65cff9e4-1fb7-4087-8c22-d5f62489d89d/artifacts/l1s993xf_IMG_7368.png",
    alt: "Run for Rett 2025 - Event Impressionen"
  },
  { 
    src: "https://customer-assets.emergentagent.com/job_65cff9e4-1fb7-4087-8c22-d5f62489d89d/artifacts/tm2qx58w_IMG_8213.jpg",
    alt: "Run for Rett 2025 - Teilnehmer"
  },
  { 
    src: "https://customer-assets.emergentagent.com/job_65cff9e4-1fb7-4087-8c22-d5f62489d89d/artifacts/ja4y7p9a_IMG_8216.jpg",
    alt: "Run for Rett 2025 - Stimmung"
  },
  { 
    src: "https://customer-assets.emergentagent.com/job_65cff9e4-1fb7-4087-8c22-d5f62489d89d/artifacts/pfrhntyf_IMG_8233.jpg",
    alt: "Run for Rett 2025 - Gemeinschaft"
  },
  { 
    src: "https://customer-assets.emergentagent.com/job_65cff9e4-1fb7-4087-8c22-d5f62489d89d/artifacts/lk6kl2m3_IMG_8246.jpg",
    alt: "Run for Rett 2025 - Laufstrecke"
  },
  { 
    src: "https://customer-assets.emergentagent.com/job_65cff9e4-1fb7-4087-8c22-d5f62489d89d/artifacts/qnao67nl_IMG_8256.jpg",
    alt: "Run for Rett 2025 - Highlights"
  },
  { 
    src: "https://customer-assets.emergentagent.com/job_65cff9e4-1fb7-4087-8c22-d5f62489d89d/artifacts/16rhbyk2_IMG_8272.jpg",
    alt: "Run for Rett 2025 - Freude"
  },
  { 
    src: "https://customer-assets.emergentagent.com/job_65cff9e4-1fb7-4087-8c22-d5f62489d89d/artifacts/qp3h3n6l_IMG_8287.jpg",
    alt: "Run for Rett 2025 - Atmosphere"
  },
  { 
    src: "https://customer-assets.emergentagent.com/job_65cff9e4-1fb7-4087-8c22-d5f62489d89d/artifacts/t9upt0wl_IMG_8296.jpg",
    alt: "Run for Rett 2025 - Zusammenhalt"
  },
  { 
    src: "https://customer-assets.emergentagent.com/job_65cff9e4-1fb7-4087-8c22-d5f62489d89d/artifacts/wmi7kot2_IMG_8303.jpg",
    alt: "Run for Rett 2025 - Begeisterung"
  },
  { 
    src: "https://customer-assets.emergentagent.com/job_65cff9e4-1fb7-4087-8c22-d5f62489d89d/artifacts/0kkg35ql_IMG_8304.jpg",
    alt: "Run for Rett 2025 - Emotionen"
  },
  { 
    src: "https://customer-assets.emergentagent.com/job_65cff9e4-1fb7-4087-8c22-d5f62489d89d/artifacts/m70wtglb_IMG_8319.jpg",
    alt: "Run for Rett 2025 - Erinnerungen"
  },
  { 
    src: "https://customer-assets.emergentagent.com/job_65cff9e4-1fb7-4087-8c22-d5f62489d89d/artifacts/8ht4dupc_IMG_8336.jpg",
    alt: "Run for Rett 2025 - Teamgeist"
  },
  { 
    src: "https://customer-assets.emergentagent.com/job_65cff9e4-1fb7-4087-8c22-d5f62489d89d/artifacts/wy5ercw1_IMG_8340.jpg",
    alt: "Run for Rett 2025 - Engagement"
  },
  { 
    src: "https://customer-assets.emergentagent.com/job_65cff9e4-1fb7-4087-8c22-d5f62489d89d/artifacts/xlh8816y_IMG_8348.jpg",
    alt: "Run for Rett 2025 - Unterstützung"
  },
  { 
    src: "https://customer-assets.emergentagent.com/job_65cff9e4-1fb7-4087-8c22-d5f62489d89d/artifacts/otu0nuir_IMG_8351.jpg",
    alt: "Run for Rett 2025 - Motivation"
  },
  { 
    src: "https://customer-assets.emergentagent.com/job_65cff9e4-1fb7-4087-8c22-d5f62489d89d/artifacts/75qcm936_IMG_8355.jpg",
    alt: "Run for Rett 2025 - Erfolg"
  },
  { 
    src: "https://customer-assets.emergentagent.com/job_65cff9e4-1fb7-4087-8c22-d5f62489d89d/artifacts/jtt6v1yv_IMG_8358.jpg",
    alt: "Run for Rett 2025 - Leidenschaft"
  },
  { 
    src: "https://customer-assets.emergentagent.com/job_65cff9e4-1fb7-4087-8c22-d5f62489d89d/artifacts/8f4sf1ag_IMG_8361.jpg",
    alt: "Run for Rett 2025 - Ausdauer"
  },
  { 
    src: "https://customer-assets.emergentagent.com/job_65cff9e4-1fb7-4087-8c22-d5f62489d89d/artifacts/8akexh9a_IMG_8375.jpg",
    alt: "Run for Rett 2025 - Stolz"
  },
  { 
    src: "https://customer-assets.emergentagent.com/job_65cff9e4-1fb7-4087-8c22-d5f62489d89d/artifacts/xwdp9abm_IMG_8381.jpg",
    alt: "Run for Rett 2025 - Zieleinlauf"
  },
  { 
    src: "https://customer-assets.emergentagent.com/job_65cff9e4-1fb7-4087-8c22-d5f62489d89d/artifacts/dpa1tql5_IMG_8383.jpg",
    alt: "Run for Rett 2025 - Glücksmomente"
  },
  { 
    src: "https://customer-assets.emergentagent.com/job_65cff9e4-1fb7-4087-8c22-d5f62489d89d/artifacts/ech539fo_IMG_8397.jpg",
    alt: "Run for Rett 2025 - Sportgeist"
  },
  { 
    src: "https://customer-assets.emergentagent.com/job_65cff9e4-1fb7-4087-8c22-d5f62489d89d/artifacts/rum0m0t8_IMG_8400.jpg",
    alt: "Run for Rett 2025 - Gemeinsam stark"
  },
  { 
    src: "https://customer-assets.emergentagent.com/job_65cff9e4-1fb7-4087-8c22-d5f62489d89d/artifacts/objg204n_IMG_8402.jpg",
    alt: "Run for Rett 2025 - Herz zeigen"
  },
  { 
    src: "https://customer-assets.emergentagent.com/job_65cff9e4-1fb7-4087-8c22-d5f62489d89d/artifacts/5u8f6wdp_IMG_8406.jpg",
    alt: "Run for Rett 2025 - Unvergesslich"
  },
  { 
    src: "https://customer-assets.emergentagent.com/job_65cff9e4-1fb7-4087-8c22-d5f62489d89d/artifacts/silrhkw5_IMG_8414.jpg",
    alt: "Run for Rett 2025 - Zusammen laufen"
  },
  { 
    src: "https://customer-assets.emergentagent.com/job_65cff9e4-1fb7-4087-8c22-d5f62489d89d/artifacts/tumm6g4w_IMG_8415.jpg",
    alt: "Run for Rett 2025 - Familienfest"
  },
  { 
    src: "https://customer-assets.emergentagent.com/job_65cff9e4-1fb7-4087-8c22-d5f62489d89d/artifacts/ntuy56ym_IMG_8425.jpg",
    alt: "Run for Rett 2025 - Freundschaft"
  },
  { 
    src: "https://customer-assets.emergentagent.com/job_65cff9e4-1fb7-4087-8c22-d5f62489d89d/artifacts/j18e59eo_IMG_8431.jpg",
    alt: "Run for Rett 2025 - Hoffnung"
  },
  { 
    src: "https://customer-assets.emergentagent.com/job_65cff9e4-1fb7-4087-8c22-d5f62489d89d/artifacts/8l3d2ef3_IMG_8432.jpg",
    alt: "Run for Rett 2025 - Solidarität"
  },
  { 
    src: "https://customer-assets.emergentagent.com/job_65cff9e4-1fb7-4087-8c22-d5f62489d89d/artifacts/l6xfmcx4_IMG_8456.jpg",
    alt: "Run for Rett 2025 - Begeisterung"
  },
  { 
    src: "https://customer-assets.emergentagent.com/job_65cff9e4-1fb7-4087-8c22-d5f62489d89d/artifacts/lvlbo6nb_IMG_8461.jpg",
    alt: "Run for Rett 2025 - Energie"
  },
  { 
    src: "https://customer-assets.emergentagent.com/job_65cff9e4-1fb7-4087-8c22-d5f62489d89d/artifacts/nocg48a1_IMG_8466.jpg",
    alt: "Run for Rett 2025 - Lebensfreude"
  },
  { 
    src: "https://customer-assets.emergentagent.com/job_65cff9e4-1fb7-4087-8c22-d5f62489d89d/artifacts/kyoumk4l_IMG_8472.jpg",
    alt: "Run for Rett 2025 - Dankbarkeit"
  },
  { 
    src: "https://customer-assets.emergentagent.com/job_65cff9e4-1fb7-4087-8c22-d5f62489d89d/artifacts/g0af0w5j_IMG_8473.jpg",
    alt: "Run for Rett 2025 - Herzlichkeit"
  },
  { 
    src: "https://customer-assets.emergentagent.com/job_65cff9e4-1fb7-4087-8c22-d5f62489d89d/artifacts/nmkxz8p1_IMG_8476.jpg",
    alt: "Run for Rett 2025 - Miteinander"
  },
  { 
    src: "https://customer-assets.emergentagent.com/job_65cff9e4-1fb7-4087-8c22-d5f62489d89d/artifacts/m3tsowt2_IMG_8478.jpg",
    alt: "Run for Rett 2025 - Verbundenheit"
  },
  { 
    src: "https://customer-assets.emergentagent.com/job_65cff9e4-1fb7-4087-8c22-d5f62489d89d/artifacts/5nzh1u03_IMG_8482.jpg",
    alt: "Run for Rett 2025 - Fröhlichkeit"
  },
  { 
    src: "https://customer-assets.emergentagent.com/job_65cff9e4-1fb7-4087-8c22-d5f62489d89d/artifacts/xfttz6nb_IMG_8487.jpg",
    alt: "Run for Rett 2025 - Ehrgeiz"
  },
  { 
    src: "https://customer-assets.emergentagent.com/job_65cff9e4-1fb7-4087-8c22-d5f62489d89d/artifacts/8rcc4nh5_IMG_8489.jpg",
    alt: "Run for Rett 2025 - Lauffreude"
  },
];

const TOKEN = "eyJhbGciOiJIUzI1NiJ9.eyJrZXkiOiI2YTA5ZDViZjk3Yzg3YjAwMDJlZTg4MzMvU0o3SXl2S2haemUiLCJleHAiOjE4MjEzMDQ4MDB9.cKA9kLvzKDyiECo8dVLNKb5HvK8m5OUI3hVYozVfnJI";
const BASE_2026 = "https://images-prod-eu.photostore24.online/6a09d5bf97c87b0002ee8833/SJ7IyvKhZze";

const gallery2026 = [
  { id: "ByqlwF3Wzg", alt: "Run for Rett 2026 - Gemeinsam am Start" },
  { id: "SkqxvF2-fl", alt: "Run for Rett 2026 - Laufstrecke" },
  { id: "B19ePKnZMg", alt: "Run for Rett 2026 - Teamgeist" },
  { id: "r1eclvF3Wzx", alt: "Run for Rett 2026 - Emotion" },
  { id: "Bk-9gDKh-ze", alt: "Run for Rett 2026 - Begeisterung" },
  { id: "Sy9evY2ZGl", alt: "Run for Rett 2026 - Zusammenhalt" },
  { id: "ByqgvYhZGx", alt: "Run for Rett 2026 - Freude" },
  { id: "rkg5gwt2ZGg", alt: "Run for Rett 2026 - Engagement" },
  { id: "HJqgvthbze", alt: "Run for Rett 2026 - Leidenschaft" },
  { id: "HJWqgwFhWfl", alt: "Run for Rett 2026 - Gemeinschaft" },
  { id: "Hkf9bDF3WGg", alt: "Run for Rett 2026 - Zieleinlauf" },
  { id: "SyeoWDF3-fx", alt: "Run for Rett 2026 - Highlights" },
  { id: "Hkhbvthbzx", alt: "Run for Rett 2026 - Erinnerungen" },
].map(img => ({ src: `${BASE_2026}/${img.id}/M.jpg?token=${TOKEN}`, alt: img.alt }));

const gallery2026_extra = [
  { src: "https://customer-assets-v7afamib.emergentagent.net/job_9fe7f796-3839-42a5-909c-1b122c8a02e3/artifacts/cx7tukm8_IMG_2676%20%281%29.jpg", alt: "Run for Rett 2026 - Läufer auf der Strecke" },
  { src: "https://customer-assets-v7afamib.emergentagent.net/job_9fe7f796-3839-42a5-909c-1b122c8a02e3/artifacts/xg8o4o0x_IMG_2663.jpg", alt: "Run for Rett 2026 - Läuferfeld" },
  { src: "https://customer-assets-v7afamib.emergentagent.net/job_9fe7f796-3839-42a5-909c-1b122c8a02e3/artifacts/z6i4ymv8_IMG_3366.jpg", alt: "Run for Rett 2026 - Gruppe auf der Strecke" },
  { src: "https://customer-assets-v7afamib.emergentagent.net/job_9fe7f796-3839-42a5-909c-1b122c8a02e3/artifacts/4b5scvxi_IMG_3370.jpg", alt: "Run for Rett 2026 - Helfer beim Aufbau" },
  { src: "https://customer-assets-v7afamib.emergentagent.net/job_9fe7f796-3839-42a5-909c-1b122c8a02e3/artifacts/2twazb2h_IMG_3379.jpg", alt: "Run for Rett 2026 - Bemalte Steine" },
  { src: "https://customer-assets-v7afamib.emergentagent.net/job_9fe7f796-3839-42a5-909c-1b122c8a02e3/artifacts/86gltn56_IMG_2957.jpg", alt: "Run for Rett 2026 - Bambini-Lauf Start" },
  { src: "https://customer-assets-v7afamib.emergentagent.net/job_9fe7f796-3839-42a5-909c-1b122c8a02e3/artifacts/hd09obnj_IMG_2936.jpg", alt: "Run for Rett 2026 - Familie beim Event" },
  { src: "https://customer-assets-v7afamib.emergentagent.net/job_9fe7f796-3839-42a5-909c-1b122c8a02e3/artifacts/cer74mfg_IMG_2759.jpg", alt: "Run for Rett 2026 - Sportliche Familie" },
  { src: "https://customer-assets-v7afamib.emergentagent.net/job_9fe7f796-3839-42a5-909c-1b122c8a02e3/artifacts/d0c0gq1q_IMG_2930.jpg", alt: "Run for Rett 2026 - Kinderlauf Menschenmenge" },
  { src: "https://customer-assets-v7afamib.emergentagent.net/job_9fe7f796-3839-42a5-909c-1b122c8a02e3/artifacts/q2xac9tv_IMG_2790.jpg", alt: "Run for Rett 2026 - Samba-Trommelgruppe" },
  { src: "https://customer-assets-v7afamib.emergentagent.net/job_9fe7f796-3839-42a5-909c-1b122c8a02e3/artifacts/k2qef3ni_IMG_3021.jpg", alt: "Run for Rett 2026 - Team Strabag" },
  { src: "https://customer-assets-v7afamib.emergentagent.net/job_9fe7f796-3839-42a5-909c-1b122c8a02e3/artifacts/bx7r1wbi_IMG_3027.jpg", alt: "Run for Rett 2026 - Jubelnde Läufer" },
  { src: "https://customer-assets-v7afamib.emergentagent.net/job_9fe7f796-3839-42a5-909c-1b122c8a02e3/artifacts/uu95eg4j_IMG_3032.jpg", alt: "Run for Rett 2026 - Helferinnen beim Basteln" },
  { src: "https://customer-assets-v7afamib.emergentagent.net/job_9fe7f796-3839-42a5-909c-1b122c8a02e3/artifacts/hd8yoz73_IMG_3036.jpg", alt: "Run for Rett 2026 - Team Kamin" },
  { src: "https://customer-assets-v7afamib.emergentagent.net/job_9fe7f796-3839-42a5-909c-1b122c8a02e3/artifacts/t8abhi8b_IMG_3039.jpg", alt: "Run for Rett 2026 - Läuferinnen" },
  { src: "https://customer-assets-v7afamib.emergentagent.net/job_9fe7f796-3839-42a5-909c-1b122c8a02e3/artifacts/3uez6mcw_IMG_3047%20%281%29.jpg", alt: "Run for Rett 2026 - Team Max Bögl" },
  { src: "https://customer-assets-v7afamib.emergentagent.net/job_9fe7f796-3839-42a5-909c-1b122c8a02e3/artifacts/guhds3re_IMG_3053.jpg", alt: "Run for Rett 2026 - Siegerinterview" },
  { src: "https://customer-assets-v7afamib.emergentagent.net/job_9fe7f796-3839-42a5-909c-1b122c8a02e3/artifacts/b25w1sx7_IMG_3050.jpg", alt: "Run for Rett 2026 - Familiengruppe" },
  { src: "https://customer-assets-v7afamib.emergentagent.net/job_9fe7f796-3839-42a5-909c-1b122c8a02e3/artifacts/8zes4zge_IMG_3061.jpg", alt: "Run for Rett 2026 - Team Freitag" },
  { src: "https://customer-assets-v7afamib.emergentagent.net/job_9fe7f796-3839-42a5-909c-1b122c8a02e3/artifacts/84hddy46_IMG_3071.jpg", alt: "Run for Rett 2026 - Rotes Team mit Hund" },
  { src: "https://customer-assets-v7afamib.emergentagent.net/job_9fe7f796-3839-42a5-909c-1b122c8a02e3/artifacts/ltflp1qo_IMG_3078.jpg", alt: "Run for Rett 2026 - Start am Zielbogen" },
  { src: "https://customer-assets-v7afamib.emergentagent.net/job_9fe7f796-3839-42a5-909c-1b122c8a02e3/artifacts/f1jx3pgo_IMG_3081.jpg", alt: "Run for Rett 2026 - Spitzengruppe am Start" },
  { src: "https://customer-assets-v7afamib.emergentagent.net/job_9fe7f796-3839-42a5-909c-1b122c8a02e3/artifacts/0stjfhx4_IMG_3092.jpg", alt: "Run for Rett 2026 - Läuferfeld am Zielbogen" },
  { src: "https://customer-assets-v7afamib.emergentagent.net/job_9fe7f796-3839-42a5-909c-1b122c8a02e3/artifacts/vnu2aymq_IMG_3090.jpg", alt: "Run for Rett 2026 - Vielfalt auf der Strecke" },
  { src: "https://customer-assets-v7afamib.emergentagent.net/job_9fe7f796-3839-42a5-909c-1b122c8a02e3/artifacts/epcq4xbd_IMG_3094.jpg", alt: "Run for Rett 2026 - Breites Läuferfeld" },
  { src: "https://customer-assets-v7afamib.emergentagent.net/job_9fe7f796-3839-42a5-909c-1b122c8a02e3/artifacts/qgyrwihm_IMG_3194.jpg", alt: "Run for Rett 2026 - Läufergruppe im Dorf" },
  { src: "https://customer-assets-v7afamib.emergentagent.net/job_9fe7f796-3839-42a5-909c-1b122c8a02e3/artifacts/vy37eng8_IMG_3201.jpg", alt: "Run for Rett 2026 - Spitzenläufer unterwegs" },
  { src: "https://customer-assets-v7afamib.emergentagent.net/job_9fe7f796-3839-42a5-909c-1b122c8a02e3/artifacts/v24jbf99_IMG_3207.jpg", alt: "Run for Rett 2026 - Läuferfeld auf der Landstraße" },
  { src: "https://customer-assets-v7afamib.emergentagent.net/job_9fe7f796-3839-42a5-909c-1b122c8a02e3/artifacts/5zy7b90m_IMG_3217.jpg", alt: "Run for Rett 2026 - Drei Generationen laufen" },
  { src: "https://customer-assets-v7afamib.emergentagent.net/job_9fe7f796-3839-42a5-909c-1b122c8a02e3/artifacts/u72vewin_IMG_3228.jpg", alt: "Run for Rett 2026 - Max Bögl Läuferinnen" },
  { src: "https://customer-assets-v7afamib.emergentagent.net/job_9fe7f796-3839-42a5-909c-1b122c8a02e3/artifacts/qw20gplx_IMG_3235.jpg", alt: "Run for Rett 2026 - Vater und Sohn unterwegs" },
  { src: "https://customer-assets-v7afamib.emergentagent.net/job_9fe7f796-3839-42a5-909c-1b122c8a02e3/artifacts/kjarm4yl_IMG_3242.jpg", alt: "Run for Rett 2026 - Läuferpaar mit Kinderwagen" },
  { src: "https://customer-assets-v7afamib.emergentagent.net/job_9fe7f796-3839-42a5-909c-1b122c8a02e3/artifacts/zu189rkv_IMG_3261.jpg", alt: "Run for Rett 2026 - Cowboy-Läufer" },
  { src: "https://customer-assets-v7afamib.emergentagent.net/job_9fe7f796-3839-42a5-909c-1b122c8a02e3/artifacts/rvfy75hw_IMG_3309.jpg", alt: "Run for Rett 2026 - Vereinsteam im Dorf" },
  { src: "https://customer-assets-v7afamib.emergentagent.net/job_9fe7f796-3839-42a5-909c-1b122c8a02e3/artifacts/82jnkegx_IMG_3351.jpg", alt: "Run for Rett 2026 - Maler Klügl jubelt" },
];

const gallery2026_all = [...gallery2026, ...gallery2026_extra];

function LightboxImage({ src, alt }) {
  const [loaded, setLoaded] = useState(false);
  useEffect(() => { setLoaded(false); }, [src]);
  return (
    <>
      {!loaded && (
        <div className="w-16 h-16 rounded-full border-4 border-white/20 border-t-white/80 animate-spin" />
      )}
      <img
        src={src}
        alt={alt}
        className={`max-h-[75vh] max-w-full object-contain transition-opacity duration-500 ${loaded ? "opacity-100" : "opacity-0 absolute"}`}
        onLoad={() => setLoaded(true)}
      />
    </>
  );
}

export default function SpendenlaufInfo() {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [activeGallery, setActiveGallery] = useState("2025");
  const [visibleCount, setVisibleCount] = useState(12);
  const [visibleCount2026, setVisibleCount2026] = useState(12);
  const location = useLocation();

  const currentGallery = activeGallery === "2025" ? gallery2025 : gallery2026_all;

  useEffect(() => {
    if (location.hash === "#galerie") {
      setTimeout(() => {
        const galerieSection = document.getElementById("galerie");
        if (galerieSection) {
          galerieSection.scrollIntoView({ behavior: "smooth" });
        }
      }, 300);
    }
  }, [location]);

  const openLightbox = (index, gallery = "2025") => {
    setActiveGallery(gallery);
    setCurrentImageIndex(index);
    setLightboxOpen(true);
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % currentGallery.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + currentGallery.length) % currentGallery.length);
  };

  const scrollToReg = () => {
    window.location.href = "/#anmeldung";
  };

  return (
    <div data-testid="spendenlauf-page" className="min-h-screen bg-white">
      {/* Header bar */}
      <div className="sticky top-0 z-50 glass-nav shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16 md:h-20">
          <Link to="/">
            <img src="/logo-runforrett.svg" alt="Run for Rett Logo" className="h-10 md:h-12 w-auto" />
          </Link>
          <Link to="/">
            <Button variant="ghost" className="text-[#b46eb6] hover:bg-[#b46eb6]/5" data-testid="spendenlauf-back-btn">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Startseite
            </Button>
          </Link>
        </div>
      </div>

      {/* Hero Banner */}
      <section className="relative py-16 md:py-24 overflow-hidden" style={{ background: "linear-gradient(135deg, #f6eef7 0%, #e8f7f0 100%)" }}>
        <div className="absolute top-10 right-0 w-80 h-80 rounded-full opacity-10" style={{ background: "#7DD4B5", filter: "blur(100px)" }} />
        <div className="absolute bottom-0 left-10 w-60 h-60 rounded-full opacity-10" style={{ background: "#b46eb6", filter: "blur(80px)" }} />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <p className="font-accent text-2xl md:text-3xl mb-3" style={{ color: "#7DD4B5" }}>
            Alle Infos auf einen Blick
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-tight mb-6" style={{ color: "#1F2937" }}>
            Alle Infos zum <span style={{ color: "#b46eb6" }}>Spendenlauf</span>
          </h1>
          <p className="text-base md:text-lg leading-relaxed max-w-2xl mx-auto mb-8" style={{ color: "#4B5563" }}>
            Mach mit beim Run for Rett – als Läufer:in, Helfer:in oder beim Kinderlauf!
            Am <strong>13. Juni 2026</strong> heißt es in Herrnried: Laufen für den guten Zweck – für Kinder mit Rett-Syndrom und ihre Familien. Egal ob groß oder klein, schnell oder gemütlich:
          </p>
          <div className="inline-flex flex-col items-center gap-2 p-4 rounded-xl" style={{ background: "rgba(180, 110, 182, 0.08)" }}>
            <p className="text-base md:text-lg font-semibold" style={{ color: "#b46eb6" }}>
              Alle Einnahmen gehen zu 100% an die Forschungseinrichtung.
            </p>
            <p className="text-sm" style={{ color: "#4B5563" }}>
              Jede Runde zählt. Jede Hilfe auch.
            </p>
          </div>
        </div>
      </section>

      {/* Participation Options */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight" style={{ color: "#1F2937" }}>
              So kannst du mitmachen
            </h2>
            <div className="section-divider mx-auto mt-4" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {/* Bambini-Lauf */}
            <Card data-testid="card-bambini" className="border-0 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 overflow-hidden">
              <div className="h-2 w-full" style={{ background: "#FFD700" }} />
              <CardContent className="p-6 md:p-8 space-y-5">
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ background: "rgba(255, 215, 0, 0.15)" }}>
                  <Baby className="w-7 h-7" style={{ color: "#FFD700" }} />
                </div>
                <h3 className="font-heading text-xl md:text-2xl font-bold" style={{ color: "#1F2937" }}>
                  Bambini-Lauf
                </h3>
                <p className="text-sm font-medium" style={{ color: "#FFD700" }}>bis 5 Jahre</p>
                <ul className="space-y-3 text-sm" style={{ color: "#4B5563" }}>
                  <li className="flex items-start gap-2">
                    <span style={{ color: "#FFD700" }} className="mt-0.5 font-bold">-</span>
                    Für die Kleinsten bis 5 Jahre
                  </li>
                  <li className="flex items-start gap-2">
                    <span style={{ color: "#FFD700" }} className="mt-0.5 font-bold">-</span>
                    Kurze Strecke zum Mitmachen
                  </li>
                  <li className="flex items-start gap-2">
                    <span style={{ color: "#FFD700" }} className="mt-0.5 font-bold">-</span>
                    Start: <strong>13:00 Uhr</strong>
                  </li>
                  <li className="flex items-start gap-2">
                    <span style={{ color: "#FFD700" }} className="mt-0.5 font-bold">-</span>
                    Eltern dürfen begleiten!
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Kinderlauf */}
            <Card data-testid="card-kinderlauf" className="border-0 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 overflow-hidden">
              <div className="h-2 w-full" style={{ background: "#7DD4B5" }} />
              <CardContent className="p-6 md:p-8 space-y-5">
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ background: "rgba(149, 225, 187, 0.15)" }}>
                  <PersonStanding className="w-7 h-7" style={{ color: "#7DD4B5" }} />
                </div>
                <h3 className="font-heading text-xl md:text-2xl font-bold" style={{ color: "#1F2937" }}>
                  Kinderlauf
                </h3>
                <p className="text-sm font-medium" style={{ color: "#7DD4B5" }}>6 - 10 Jahre</p>
                <ul className="space-y-3 text-sm" style={{ color: "#4B5563" }}>
                  <li className="flex items-start gap-2">
                    <span style={{ color: "#7DD4B5" }} className="mt-0.5 font-bold">-</span>
                    Für Kinder von 6 bis 10 Jahren
                  </li>
                  <li className="flex items-start gap-2">
                    <span style={{ color: "#7DD4B5" }} className="mt-0.5 font-bold">-</span>
                    Strecke: ca. 1 km
                  </li>
                  <li className="flex items-start gap-2">
                    <span style={{ color: "#7DD4B5" }} className="mt-0.5 font-bold">-</span>
                    Start: <strong>13:30 Uhr</strong>
                  </li>
                  <li className="flex items-start gap-2">
                    <span style={{ color: "#7DD4B5" }} className="mt-0.5 font-bold">-</span>
                    Jedes Kind erhält eine Überraschung
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Läufer:in */}
            <Card data-testid="card-laeufer" className="border-0 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 overflow-hidden">
              <div className="h-2 w-full" style={{ background: "#b46eb6" }} />
              <CardContent className="p-6 md:p-8 space-y-5">
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ background: "rgba(180, 110, 182, 0.1)" }}>
                  <PersonStanding className="w-7 h-7" style={{ color: "#b46eb6" }} />
                </div>
                <h3 className="font-heading text-xl md:text-2xl font-bold" style={{ color: "#1F2937" }}>
                  Erwachsenenlauf
                </h3>
                <p className="text-sm font-medium" style={{ color: "#b46eb6" }}>ab 11 Jahren</p>
                <ul className="space-y-3 text-sm" style={{ color: "#4B5563" }}>
                  <li className="flex items-start gap-2">
                    <span style={{ color: "#b46eb6" }} className="mt-0.5 font-bold">-</span>
                    Start: <strong>14 - 18 Uhr</strong>
                  </li>
                  <li className="flex items-start gap-2">
                    <span style={{ color: "#b46eb6" }} className="mt-0.5 font-bold">-</span>
                    Ca. 1 km pro Runde
                  </li>
                  <li className="flex items-start gap-2">
                    <span style={{ color: "#b46eb6" }} className="mt-0.5 font-bold">-</span>
                    Laufe so viele Runden wie du möchtest
                  </li>
                  <li className="flex items-start gap-2">
                    <span style={{ color: "#b46eb6" }} className="mt-0.5 font-bold">-</span>
                    Du bestimmst dein Tempo
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Helfer:in */}
            <Card data-testid="card-helfer" className="border-0 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 overflow-hidden">
              <div className="h-2 w-full" style={{ background: "linear-gradient(90deg, #b46eb6, #7DD4B5)" }} />
              <CardContent className="p-6 md:p-8 space-y-5">
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ background: "linear-gradient(135deg, rgba(180,110,182,0.1), rgba(149,225,187,0.1))" }}>
                  <HandHelping className="w-7 h-7" style={{ color: "#b46eb6" }} />
                </div>
                <h3 className="font-heading text-xl md:text-2xl font-bold" style={{ color: "#1F2937" }}>
                  Als Helfer:in
                </h3>
                <p className="text-sm font-medium" style={{ color: "#b46eb6" }}>Jede Hand zählt</p>
                <ul className="space-y-3 text-sm" style={{ color: "#4B5563" }}>
                  <li className="flex items-start gap-2">
                    <span style={{ color: "#b46eb6" }} className="mt-0.5 font-bold">-</span>
                    Unterstütze uns beim Aufbau, während des Events oder beim Abbau
                  </li>
                  <li className="flex items-start gap-2">
                    <span style={{ color: "#b46eb6" }} className="mt-0.5 font-bold">-</span>
                    Verpflegungsstände, Rundenzählen, Streckensicherung u.v.m.
                  </li>
                  <li className="flex items-start gap-2">
                    <span style={{ color: "#b46eb6" }} className="mt-0.5 font-bold">-</span>
                    Jede helfende Hand macht den Tag unvergesslich!
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Event Details */}
      <section className="py-16 md:py-24" style={{ background: "#F8F9FA" }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight" style={{ color: "#1F2937" }}>
              Ort & Termin
            </h2>
            <div className="section-divider mx-auto mt-4" />
          </div>

          <Card className="border-0 shadow-xl overflow-hidden">
            <div className="h-2 w-full" style={{ background: "linear-gradient(90deg, #b46eb6, #7DD4B5)" }} />
            <CardContent className="p-8 md:p-12">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0" style={{ background: "rgba(149, 225, 187, 0.15)" }}>
                    <MapPin className="w-6 h-6" style={{ color: "#7DD4B5" }} />
                  </div>
                  <div>
                    <p className="font-heading font-bold text-base mb-1" style={{ color: "#1F2937" }}>Ort</p>
                    <p className="text-sm" style={{ color: "#4B5563" }}>Herrnried</p>
                    <p className="text-sm" style={{ color: "#4B5563" }}>Start/Ziel: <strong>Feuerwehrhaus Herrnried</strong></p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0" style={{ background: "rgba(180, 110, 182, 0.1)" }}>
                    <CalendarDays className="w-6 h-6" style={{ color: "#b46eb6" }} />
                  </div>
                  <div>
                    <p className="font-heading font-bold text-base mb-1" style={{ color: "#1F2937" }}>Datum</p>
                    <p className="text-sm" style={{ color: "#4B5563" }}>Samstag, <strong>13.06.2026</strong></p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0" style={{ background: "rgba(149, 225, 187, 0.15)" }}>
                    <Clock className="w-6 h-6" style={{ color: "#7DD4B5" }} />
                  </div>
                  <div>
                    <p className="font-heading font-bold text-base mb-1" style={{ color: "#1F2937" }}>Zeitplan</p>
                    <p className="text-sm" style={{ color: "#4B5563" }}>13:00 Uhr – Bambini-Lauf</p>
                    <p className="text-sm" style={{ color: "#4B5563" }}>13:30 Uhr – Kinderlauf</p>
                    <p className="text-sm" style={{ color: "#4B5563" }}>14:00 – 18:00 Uhr – Erwachsenenlauf</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0" style={{ background: "rgba(180, 110, 182, 0.1)" }}>
                    <Mail className="w-6 h-6" style={{ color: "#b46eb6" }} />
                  </div>
                  <div>
                    <p className="font-heading font-bold text-base mb-1" style={{ color: "#1F2937" }}>Anmeldung & Infos</p>
                    <p className="text-sm" style={{ color: "#4B5563" }}>Das Anmeldeformular findest du direkt auf der Startseite.</p>
                  </div>
                </div>
              </div>

              <div className="mt-10 flex flex-wrap justify-center gap-4">
                <Link to="/#anmeldung">
                  <Button
                    data-testid="info-register-btn"
                    className="rounded-full px-8 py-6 text-base font-bold bg-[#b46eb6] hover:bg-[#8f5691] text-white shadow-lg hover:shadow-xl transition-all hover:-translate-y-1"
                  >
                    Zur Anmeldung
                  </Button>
                </Link>
                <a
                  href="https://www.instagram.com/run.for.rett/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button
                    data-testid="info-instagram-btn"
                    variant="outline"
                    className="rounded-full px-8 py-6 text-base font-semibold border-2 border-[#b46eb6]/20 hover:border-[#b46eb6] text-[#b46eb6] transition-all"
                  >
                    <Instagram className="w-5 h-5 mr-2" />
                    @run.for.rett
                  </Button>
                </a>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Gallery 2025 */}
      <section id="galerie" className="py-16 md:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4" style={{ background: "rgba(180, 110, 182, 0.1)" }}>
              <Camera className="w-4 h-4" style={{ color: "#b46eb6" }} />
              <span className="text-sm font-semibold" style={{ color: "#b46eb6" }}>Rückblick</span>
            </div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight" style={{ color: "#1F2937" }}>
              Impressionen 2025
            </h2>
            <p className="text-base mt-3 max-w-xl mx-auto" style={{ color: "#4B5563" }}>
              Ein Blick zurück auf unser erstes Event – Emotionen, Gemeinschaft und unvergessliche Momente.
            </p>
            <div className="section-divider mx-auto mt-4" />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            {gallery2025.slice(0, visibleCount).map((img, i) => (
              <LazyGalleryImage
                key={i}
                index={i}
                src={img.src}
                alt={img.alt}
                onClick={() => openLightbox(i)}
              />
            ))}
          </div>

          {visibleCount < gallery2025.length && (
            <div className="text-center mt-8">
              <Button
                data-testid="gallery-load-more"
                onClick={() => setVisibleCount((v) => Math.min(v + 12, gallery2025.length))}
                variant="outline"
                className="rounded-full px-8 py-5 text-base font-semibold border-2 border-[#b46eb6]/20 hover:border-[#b46eb6] text-[#b46eb6] transition-all"
              >
                Mehr Bilder laden ({gallery2025.length - visibleCount} weitere)
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
        <DialogContent className="max-w-5xl w-full p-0 bg-black/95 border-0 overflow-hidden">
          <div className="relative flex items-center justify-center min-h-[60vh] md:min-h-[80vh]">
            <button
              onClick={() => setLightboxOpen(false)}
              className="absolute top-4 right-4 z-50 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
            >
              <X className="w-5 h-5 text-white" />
            </button>
            <button
              onClick={prevImage}
              className="absolute left-4 z-50 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
            >
              <ChevronLeft className="w-6 h-6 text-white" />
            </button>
            {lightboxOpen && (
              <LightboxImage
                src={currentGallery[currentImageIndex]?.src}
                alt={currentGallery[currentImageIndex]?.alt}
              />
            )}
            <button
              onClick={nextImage}
              className="absolute right-4 z-50 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
            >
              <ChevronRight className="w-6 h-6 text-white" />
            </button>
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full bg-white/10">
              <p className="text-white text-sm font-medium">
                {currentImageIndex + 1} / {currentGallery.length}
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Gallery 2026 */}
      <section id="galerie-2026" className="py-16 md:py-24" style={{ background: "#F8F9FA" }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4" style={{ background: "rgba(125, 212, 181, 0.15)" }}>
              <Camera className="w-4 h-4" style={{ color: "#7DD4B5" }} />
              <span className="text-sm font-semibold" style={{ color: "#7DD4B5" }}>Rückblick</span>
            </div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight" style={{ color: "#1F2937" }}>
              Impressionen 2026
            </h2>
            <p className="text-base mt-3 max-w-xl mx-auto" style={{ color: "#4B5563" }}>
              Der zweite Run for Rett – noch größer, noch emotionaler. Fotos von <a href="https://sandra-hirschmann.de/" target="_blank" rel="noopener noreferrer" className="font-semibold underline underline-offset-2 hover:no-underline" style={{ color: "#b46eb6" }}>Sandra Hirschmann</a>.
            </p>
            <div className="section-divider mx-auto mt-4" />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            {gallery2026_all.slice(0, visibleCount2026).map((img, i) => (
              <LazyGalleryImage
                key={`2026-${i}`}
                index={i}
                src={img.src}
                alt={img.alt}
                onClick={() => openLightbox(i, "2026")}
              />
            ))}
          </div>

          {visibleCount2026 < gallery2026_all.length && (
            <div className="text-center mt-8">
              <Button
                data-testid="gallery-2026-load-more"
                onClick={() => setVisibleCount2026((v) => Math.min(v + 12, gallery2026_all.length))}
                variant="outline"
                className="rounded-full px-8 py-5 text-base font-semibold border-2 border-[#7DD4B5]/30 hover:border-[#7DD4B5] text-[#7DD4B5] transition-all"
              >
                Mehr Bilder laden ({gallery2026_all.length - visibleCount2026} weitere)
              </Button>
            </div>
          )}

          <p className="text-center mt-6 text-xs" style={{ color: "#9CA3AF" }}>
            Fotografie: Sandra Hirschmann | <a href="https://sandra-hirschmann.de/" target="_blank" rel="noopener noreferrer" className="underline hover:no-underline">sandra-hirschmann.de</a>
          </p>
        </div>
      </section>

      {/* Sponsors */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight" style={{ color: "#1F2937" }}>
              Unsere Sponsoren
            </h2>
            <div className="section-divider mx-auto mt-4" />
          </div>

          {/* Hauptsponsoren */}
          <div className="mb-12">
            <h3 className="font-heading text-lg font-semibold text-center mb-6" style={{ color: "#b46eb6" }}>
              Hauptsponsoren
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6">
              {mainSponsors.map((s, i) => (
                <a
                  key={i}
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`main-sponsor-${i}`}
                  className="flex items-center justify-center rounded-xl border border-gray-100 bg-white hover:shadow-lg hover:border-[#b46eb6]/30 transition-all p-6 md:p-8 cursor-pointer"
                >
                  <img
                    src={s.logo}
                    alt={s.name}
                    className="max-h-20 md:max-h-24 w-auto object-contain"
                    loading="lazy"
                  />
                </a>
              ))}
            </div>
          </div>

          {/* Partnersponsoren */}
          <div>
            <h3 className="font-heading text-lg font-semibold text-center mb-6" style={{ color: "#b46eb6" }}>
              Partnersponsoren
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 md:gap-4">
              {partnerSponsors.slice(0, 10).map((s, i) => 
                s.url ? (
                  <a
                    key={i}
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`partner-sponsor-${i}`}
                    className="flex items-center justify-center rounded-xl border border-gray-100 bg-white hover:shadow-lg hover:border-[#b46eb6]/30 transition-all p-4 md:p-6 cursor-pointer"
                  >
                    <img src={s.logo} alt={s.name} className="max-h-14 md:max-h-16 w-auto object-contain" loading="lazy" />
                  </a>
                ) : (
                  <div
                    key={i}
                    data-testid={`partner-sponsor-${i}`}
                    className="flex items-center justify-center rounded-xl border border-gray-100 bg-white hover:shadow-md transition-all p-4 md:p-6"
                  >
                    <img src={s.logo} alt={s.name} className="max-h-14 md:max-h-16 w-auto object-contain" loading="lazy" />
                  </div>
                )
              )}
            </div>
            <div className="flex flex-wrap justify-center gap-3 md:gap-4 mt-3 md:mt-4">
              {partnerSponsors.slice(10).map((s, i) => (
                <a
                  key={i + 10}
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`partner-sponsor-${i + 10}`}
                  className="flex items-center justify-center rounded-xl border border-gray-100 bg-white hover:shadow-lg hover:border-[#b46eb6]/30 transition-all p-4 md:p-6 cursor-pointer w-[calc(50%-6px)] sm:w-[calc(33.333%-8px)] md:w-[calc(20%-13px)]"
                >
                  <img src={s.logo} alt={s.name} className="max-h-14 md:max-h-16 w-auto object-contain" loading="lazy" />
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-16 md:py-20 relative overflow-hidden" style={{ background: "#b46eb6" }}>
        <div className="absolute top-0 right-0 w-72 h-72 rounded-full opacity-10" style={{ background: "#7DD4B5", filter: "blur(80px)" }} />
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
            Gemeinsam machen wir diesen Tag zu etwas ganz Besonderem
          </h2>
          <p className="text-base md:text-lg mb-8" style={{ color: "rgba(255,255,255,0.85)" }}>
            Für Kinder, für Familien, für Hoffnung!
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/#anmeldung">
              <Button
                data-testid="cta-register-btn"
                className="rounded-full px-8 py-6 text-base font-bold bg-white hover:bg-gray-50 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1"
                style={{ color: "#b46eb6" }}
              >
                Jetzt anmelden
              </Button>
            </Link>
            <Link to="/#spenden">
              <Button
                data-testid="cta-donate-btn"
                variant="outline"
                className="rounded-full px-8 py-6 text-base font-semibold border-2 border-white/30 text-white hover:bg-white/10 transition-all"
              >
                Spenden
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10" style={{ background: "#1F2937" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img src="/logo-runforrett.svg" alt="Run for Rett Logo" className="h-8 w-auto brightness-0 invert" />
              <p className="text-sm" style={{ color: "rgba(255,255,255,0.5)" }}>
                Laufen für eine Zukunft ohne Rett-Syndrom
              </p>
            </div>
            <div className="flex items-center gap-4">
              <a
                data-testid="info-social-instagram"
                href="https://www.instagram.com/run.for.rett/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-full flex items-center justify-center hover:scale-110 transition-transform"
                style={{ background: "rgba(149,225,187,0.15)" }}
              >
                <Instagram className="w-4 h-4" style={{ color: "#7DD4B5" }} />
              </a>
              <a
                data-testid="info-social-whatsapp"
                href="https://wa.me/?text=Run%20for%20Rett%20-%20Laufen%20f%C3%BCr%20eine%20Zukunft%20ohne%20Rett-Syndrom!"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-full flex items-center justify-center hover:scale-110 transition-transform"
                style={{ background: "rgba(149,225,187,0.15)" }}
              >
                <MessageCircle className="w-4 h-4" style={{ color: "#7DD4B5" }} />
              </a>
            </div>
            <div className="flex items-center gap-4 text-sm" style={{ color: "rgba(255,255,255,0.4)" }}>
              <Link to="/impressum" className="hover:text-white transition-colors">Impressum</Link>
              <Link to="/datenschutz" className="hover:text-white transition-colors">Datenschutz</Link>
              <a href="mailto:info@runforrett.de" className="hover:text-white transition-colors">Kontakt</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
